import React, {useEffect, useState} from 'react';
import {getReportById, updateReportDoc} from "../../../../firebase/firestore/reports";
import {useParams} from "react-router";
import {Link as RouterLink} from 'react-router';
import {useReportStore} from "../../../../features/products/useReportStore";
import {Alert, Button, Divider, Stack, Typography} from "@mui/material";
import ProductHeader from "./components/productHeader/ProductHeader";
import ReportPreviewPage from "../../../../features/products/previewPage/ReportPreviewPage";
import ValidationForm from './components/validationForm/ValidationForm';
import LoadingInTheMiddle from "../../../../components/LoadingInTheMiddle";
import {
    getValidationStepsForProductType
} from './constants/validationStepSelectors';

class PanelErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
        return {hasError: true};
    }

    componentDidCatch(error) {
        console.error('[ProductPage] Panel render failed:', error);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({hasError: false});
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

const ProductPage = () => {

    const [isLoading, setIsLoading] = useState(true)
    // store previous document.title so we can restore it on unmount
    const prevTitleRef = React.useRef(typeof document !== 'undefined' ? document.title : '');
    const setReport = useReportStore(s => s.setReport)
    const reportStatus = useReportStore(s => s.status);
    const {reportId} = useParams()
    const [notFound, setNotFound] = useState(false)
    const [loadError, setLoadError] = useState(null)
    const [reloadKey, setReloadKey] = useState(0)

    // local: which validation step is active in the UI
    const [activeStep, setActiveStep] = useState(0);

    const [productTypeValue, setProductTypeValue] = useState(null);
    const isDraft = reportStatus === 'Draft';


    useEffect(() => {
        let mounted = true;
        // capture current title at the start of this effect (in case of reportId changes)
        if (typeof document !== 'undefined') prevTitleRef.current = document.title;
        setNotFound(false);
        setLoadError(null);
        setIsLoading(true);

        const loadReport = async () => {
            try {
                const reportData = await getReportById(reportId);
                if (!mounted) return;
                if (!reportData) {
                    setNotFound(true);
                    return;
                }

                // Check if this is a disclosure
                const currentProductType = reportData.productType?.value || null;
                setProductTypeValue(currentProductType);

                // Use appropriate validation steps based on product type
                const validationSteps = getValidationStepsForProductType(currentProductType);

                // Ensure validation exists: if missing, create initial entry (step 0)
                let reportToSet = reportData;
                const shouldInitValidation = !reportData.validation &&
                    (reportData.status === 'Awaiting Review' || reportData.status === 'approved');
                if (shouldInitValidation) {
                    const initialValidation = {0: {passed: undefined, feedback: ''}};
                    reportToSet = {...reportData, validation: initialValidation};
                    // persist to Firestore but don't block UI
                    updateReportDoc(reportId, {validation: initialValidation}).catch(err => {
                        console.error('Failed to initialize validation field in Firestore', err);
                    });
                }

                // set into zustand store
                setReport(reportToSet);

                // compute active step: next index after the last passed === true
                const validation = reportToSet.validation || {};
                const passedIndices = Object.keys(validation)
                    .map(k => Number(k))
                    .filter(i => validation[i] && validation[i].passed === true)
                    .sort((a, b) => a - b);

                const lastPassed = passedIndices.length ? passedIndices[passedIndices.length - 1] : -1;
                let nextStep = lastPassed + 1;
                // clamp to available steps (0..validationSteps.length-1)
                const maxStep = Math.max(0, validationSteps.length - 1);
                if (nextStep > maxStep) nextStep = maxStep;

                setActiveStep(nextStep);

                // Set the browser tab title to the report's title (if available)
                try {
                    if (typeof document !== 'undefined' && reportToSet && reportToSet.title) {
                        document.title = reportToSet.title;
                    }
                } catch (err) {
                    console.error('Failed to set document.title:', err);
                }
            } catch (error) {
                console.error('Error fetching report data:', error);
                if (mounted) {
                    setLoadError(error);
                }
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        if (reportId) loadReport();

        return () => {
            // restore previous title on cleanup
            try {
                if (typeof document !== 'undefined') document.title = prevTitleRef.current;
            } catch (err) {
                // ignore
            }
            mounted = false;
        };
    }, [reportId, reloadKey, setReport]);


    // First, show not-found if applicable
    if (notFound) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%" spacing={2} p={4}>
                <Typography variant="h6">Report not found</Typography>
                <Button component={RouterLink} to="/products" variant="contained">Back to Products</Button>
            </Stack>
        );
    }

    if (loadError) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%" spacing={2} p={4}>
                <Alert severity="error" sx={{maxWidth: 640}}>
                    We could not load this draft right now. Please retry or return to the products list.
                </Alert>
                <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={() => setReloadKey(prev => prev + 1)}>Retry</Button>
                    <Button component={RouterLink} to="/products" variant="outlined">Back to Products</Button>
                </Stack>
            </Stack>
        );
    }

    if (isLoading) {
        return <LoadingInTheMiddle/>;
    }

    return (
        <>
            <ProductHeader
                activeStep={activeStep}
                onStepChange={setActiveStep}
                showValidationStepper={!isDraft}
                productTypeValue={productTypeValue}
            />

            <Divider/>
            <Stack direction={'row'} spacing={2} p={2}>
                <Stack flexGrow={1} minWidth={0}>
                    <PanelErrorBoundary
                        resetKey={`${reportId}-${activeStep}-preview`}
                        fallback={
                            <Alert severity="warning">
                                Preview is temporarily unavailable for this draft.
                            </Alert>
                        }
                    >
                        <ReportPreviewPage/>
                    </PanelErrorBoundary>
                </Stack>

                <Stack minWidth={'400px'} maxWidth={'520px'} flexShrink={0}>
                    {isDraft ? (
                        <Alert severity="info">
                            The report hasn't been submitted for review yet
                        </Alert>
                    ) : (
                        <PanelErrorBoundary
                            resetKey={`${reportId}-${activeStep}-validation`}
                            fallback={
                                <Stack spacing={1}>
                                    <Alert severity="warning">
                                        Validation form is temporarily unavailable for this draft.
                                    </Alert>
                                    <Button variant="outlined" onClick={() => setReloadKey(prev => prev + 1)}>
                                        Reload Draft
                                    </Button>
                                </Stack>
                            }
                        >
                            <ValidationForm
                                stepIndex={activeStep}
                                productTypeValue={productTypeValue}
                            />
                        </PanelErrorBoundary>
                    )}
                </Stack>

            </Stack>
        </>
    )

};

export default ProductPage;